import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { MarkdownRenderer } from './MarkdownRenderer';
import { useTheme } from '@/contexts/ThemeContext';

interface LiveMarkdownEditorProps {
    value: string;
    onSave: (value: string) => void;
    onCancel: () => void;
    components?: Record<string, React.ComponentType<any>>;
}

export const LiveMarkdownEditor: React.FC<LiveMarkdownEditorProps> = ({
    value,
    onSave,
    onCancel,
    components = {}
}) => {
    const [editValue, setEditValue] = useState(value);
    const [textareaHeight, setTextareaHeight] = useState(200);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { isDark } = useTheme();

    // Auto-resize textarea and sync height
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            const newHeight = Math.max(textarea.scrollHeight, 200);
            textarea.style.height = newHeight + 'px';
            setTextareaHeight(newHeight);
        }
    }, [editValue]);

    // Focus textarea on mount
    useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    return (
        <div className="space-y-4 w-full">
            {/* Split view: Editor on left, Preview on right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 w-full">
                {/* Markdown Editor */}
                <div className="space-y-2">
                    <div className="text-sm font-medium text-muted-foreground">
                        Markdown Editor
                    </div>
                    <textarea
                        ref={textareaRef}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        placeholder="Write your content in Markdown...&#10;&#10;You can use:&#10;• **bold** and *italic*&#10;• # Headings&#10;• - Lists&#10;• [Links](url)&#10;• And custom components like <CompanyName />"
                        className="w-full resize-none overflow-hidden rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        style={{
                            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                            minHeight: '200px',
                            lineHeight: '1.5'
                        }}
                    />
                </div>

                {/* Live Preview */}
                <div className="space-y-2">
                    <div className="text-sm font-medium text-muted-foreground">
                        Live Preview
                    </div>
                    <div 
                        className="rounded-md border border-input bg-muted/30 px-3 py-2"
                        style={{
                            minHeight: `${textareaHeight}px`,
                            height: `${textareaHeight}px`,
                            overflowY: 'auto'
                        }}
                    >
                        {editValue ? (
                            <MarkdownRenderer source={editValue} components={components} noPadding />
                        ) : (
                            <div className="text-muted-foreground text-sm italic">
                                Start typing to see your content rendered...
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
                <Button
                    variant="outline"
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => onSave(editValue)}
                >
                    Save
                </Button>
            </div>
        </div>
    );
};
